import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

interface StoredRoute {
  handle: DetachedRouteHandle;
  storeNumber: string;
}

/**
 * Keeps the Device / WAN / LAN / WiFi health pages alive when switching between
 * the health tabs so their search is not re-run on every navigation. A page is
 * only re-created (and therefore re-fetched) when the `storeNumber` query param
 * differs from the value the page was stored with, i.e. when a new store number
 * has actually been searched. Routes opt in via `data: { reuse: true }`; all
 * other routes fall through to Angular's default (no caching) behavior.
 */
export class HealthRouteReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, StoredRoute>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data?.['reuse'] === true;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.getRouteKey(route);

    if (handle == null) {
      this.storedRoutes.delete(key);
      return;
    }

    this.storedRoutes.set(key, {
      handle,
      storeNumber: this.getStoreNumber(route)
    });
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (route.data?.['reuse'] !== true) {
      return false;
    }

    const key = this.getRouteKey(route);
    const stored = this.storedRoutes.get(key);

    if (!stored) {
      return false;
    }

    // Only reuse the cached page if the store number hasn't changed; otherwise
    // drop it so the component is re-created and the search runs again.
    if (stored.storeNumber !== this.getStoreNumber(route)) {
      this.storedRoutes.delete(key);
      return false;
    }

    return true;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (route.data?.['reuse'] !== true) {
      return null;
    }
    return this.storedRoutes.get(this.getRouteKey(route))?.handle ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * The health leaf routes share an empty config path, so build a stable key
   * from the full resolved URL (e.g. "health/device", "health/wan").
   */
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(segment => segment.url.map(url => url.path).join('/'))
      .filter(part => part.length > 0)
      .join('/');
  }

  private getStoreNumber(route: ActivatedRouteSnapshot): string {
    return route.queryParams?.['storeNumber'] ?? '';
  }
}
